/**
 * Store Zustand pour la gestion de l'état du jeu
 */

import { create } from 'zustand';
import type { GameState, Position, WallOrientation, LastAction } from '@pathblock/core';

type GameView = 'lobby' | 'waiting' | 'playing' | 'finished' | 'matchmaking';

interface WallPreview {
  row: number;
  col: number;
  orientation: WallOrientation;
}

interface GameStore {
  // État de connexion
  connected: boolean;
  roomId: string | null;
  playerId: 1 | 2 | null;
  playerName: string;

  // État du jeu
  gameState: GameState | null;
  view: GameView;

  // UI
  wallPreview: WallPreview | null;
  placingWall: boolean;
  wallOrientation: WallOrientation;
  remainingTime: number | null;
  error: string | null;
  endMessage: string | null;
  opponentDisconnected: boolean;
  matchmakingActive: boolean;
  isVsAI: boolean;
  gameStartTime: number | null;
  moveHistory: LastAction[];

  // Actions de connexion
  setConnected: (connected: boolean) => void;
  setRoomId: (roomId: string | null) => void;
  setPlayerId: (playerId: 1 | 2 | null) => void;
  setPlayerName: (name: string) => void;

  // Actions de jeu
  setGameState: (state: GameState | null) => void;
  setView: (view: GameView) => void;

  // Actions UI
  setWallPreview: (preview: WallPreview | null) => void;
  setPlacingWall: (placing: boolean) => void;
  toggleWallOrientation: () => void;
  setRemainingTime: (time: number | null) => void;
  setError: (error: string | null) => void;
  setEndMessage: (message: string | null) => void;
  setIsVsAI: (vsAI: boolean) => void;
  setGameStartTime: (time: number | null) => void;
  addMoveHistory: (action: LastAction) => void;

  // Helpers
  isMyTurn: () => boolean;
  getMyPlayer: () => { position: Position; wallsRemaining: number } | null;
  reset: () => void;
}

const initialState = {
  connected: false,
  roomId: null,
  playerId: null,
  playerName: '',
  gameState: null,
  view: 'lobby' as GameView,
  wallPreview: null,
  placingWall: false,
  wallOrientation: 'horizontal' as WallOrientation,
  remainingTime: null,
  error: null,
  endMessage: null,
  opponentDisconnected: false,
  matchmakingActive: false,
  isVsAI: false,
  gameStartTime: null as number | null,
  moveHistory: [] as LastAction[],
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initialState,

  // Actions de connexion
  setConnected: (connected) => set({ connected }),
  setRoomId: (roomId) => set({ roomId }),
  setPlayerId: (playerId) => set({ playerId }),
  setPlayerName: (playerName) => set({ playerName }),

  // Actions de jeu
  setGameState: (gameState) => set({ gameState }),
  setView: (view) => set({ view }),

  // Actions UI
  setWallPreview: (wallPreview) => set({ wallPreview }),
  setPlacingWall: (placingWall) => set({ placingWall, wallPreview: placingWall ? get().wallPreview : null }),
  toggleWallOrientation: () =>
    set((state) => ({
      wallOrientation: state.wallOrientation === 'horizontal' ? 'vertical' : 'horizontal',
      wallPreview: state.wallPreview
        ? {
            ...state.wallPreview,
            orientation: state.wallOrientation === 'horizontal' ? 'vertical' : 'horizontal',
          }
        : null,
    })),
  setRemainingTime: (remainingTime) => set({ remainingTime }),
  setError: (error) => set({ error }),
  setEndMessage: (endMessage) => set({ endMessage }),
  setIsVsAI: (isVsAI) => set({ isVsAI }),
  setGameStartTime: (gameStartTime) => set({ gameStartTime }),
  addMoveHistory: (action) => set((state) => ({
    moveHistory: [...state.moveHistory.slice(-19), action],
  })),

  // Helpers
  isMyTurn: () => {
    const { gameState, playerId } = get();
    return gameState?.currentPlayer === playerId;
  },

  getMyPlayer: () => {
    const { gameState, playerId } = get();
    if (!gameState || !playerId) return null;
    const player = gameState.players[playerId - 1];
    return {
      position: player.position,
      wallsRemaining: player.wallsRemaining,
    };
  },

  reset: () => set({ ...initialState, playerName: get().playerName, moveHistory: [] }),
}));
